import React from 'react'
import {FormattedMessage} from 'react-intl'
import {
    Flex,
    Text,
    Button,
    SimpleGrid,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductRegistrationCard from '../../product-registration/partials/product-registration-card'
import {
    GuaranteedIcon,
    SupportIcon,
    DiscountIcon,
    ManualsIcon,
    Icon360,
} from '../../../components/icons'
import routingObject from '../../../utils/static-routes'
import {useCustomPreferenceWithLocale} from '../../../hooks/use-custom-preference'
import {BRAND_KEY} from '../../../brand'
import useProductRegistrationHandler from '../../../custom-hooks/use-product-registration-handler'

const RegisteredProductWidget = () => {
    const styles = useMultiStyleConfig('Account')
    const handleProductRegistrationClick = useProductRegistrationHandler(routingObject)
    const isWarrantyBox = useCustomPreferenceWithLocale('showWarrantyBox')
    const isWarrantyBoxEnabled = isWarrantyBox?.box === 'true'

    const Icon = BRAND_KEY === 'kenwood' ? Icon360 : ManualsIcon

    const cardsData = [
        isWarrantyBoxEnabled && {
            icon: GuaranteedIcon,
            description: (
                <FormattedMessage
                    id="product_registration.guaranteedIcon_description"
                    defaultMessage="Extended warranty on selected items. Check <u>eligible products</u>"
                    values={{
                        u: (chunks) => (
                            <u>
                                <a href={isWarrantyBox?.hreflink}>{chunks}</a>
                            </u>
                        ),
                    }}
                />
            ),
            testId: 'warranty-card',
        },
        {
            icon: Icon,
            description: (
                <FormattedMessage
                    id="product_registration.icon360_description"
                    defaultMessage="Easy access to your product information and helpful tips"
                />
            ),
            testId: 'manuals-card',
        },
        {
            icon: DiscountIcon,
            description: (
                <FormattedMessage
                    id="product_registration.discountIcon_description"
                    defaultMessage="Exclusive offers with newsletter sign up"
                />
            ),
            testId: 'discount-card',
        },
        {
            icon: SupportIcon,
            description: (
                <FormattedMessage
                    id="product_registration.supportIcon_description"
                    defaultMessage="Direct access to our dedicated product service and support teams"
                />
            ),
            testId: 'support-card',
        },
    ].filter(Boolean)

    return (
        <Flex {...styles.productRegistrationBox} data-testid="registered-products-widget">
            <Flex direction="column" gap={6}>
                <Text {...styles.title}>
                    <FormattedMessage
                        id="product_registration.title"
                        defaultMessage="Register your product"
                    />
                </Text>
                <Text {...styles.description}>
                    <FormattedMessage
                        id="product_registration.registered_product_widget.why_register"
                        defaultMessage="Why register your product? Get useful tips on how to keep it working at its best
                    throughout its lifetime and gain access to the latest news and exclusive offers."
                    />
                </Text>
                <Button
                    {...styles.registerButton}
                    onClick={() => handleProductRegistrationClick()}
                    variant="primary"
                >
                    <FormattedMessage
                        id="product_registration.button"
                        defaultMessage="Register your product now"
                    />
                </Button>
            </Flex>
            <Flex direction="column" gap={6}>
                <Text {...styles.moreBenefits}>
                    <FormattedMessage
                        id="product_registration.registered_product_widget.more_benefits"
                        defaultMessage="More benefits"
                    />
                </Text>
                <SimpleGrid {...styles.cardGrid}>
                    {cardsData.map((card, index) => (
                        <ProductRegistrationCard
                            key={index}
                            icon={card.icon}
                            title={card.description}
                            variant="account-product-registration"
                        />
                    ))}
                </SimpleGrid>
            </Flex>
        </Flex>
    )
}

export default RegisteredProductWidget
